import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { type Locale, type TranslationKey, translations } from './translations';

interface LocaleContextType {
	locale: Locale;
	setLocale: (locale: Locale) => void;
	t: (key: TranslationKey) => string;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

const getInitialLocale = (): Locale => {
	if (typeof window === 'undefined') {
		return 'ru';
	}

	const stored = localStorage.getItem('locale');
	// Validate that stored value is a valid Locale, since localStorage can contain any string
	if (stored === 'en' || stored === 'ru') {
		return stored;
	}

	const browserLang = navigator.language.split('-')[0];

	return browserLang === 'en' ? 'en' : 'ru';
};

interface LocaleProviderProps {
	children: ReactNode;
}

export const LocaleProvider: FC<LocaleProviderProps> = ({ children }) => {
	const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

	const setLocale = useCallback((newLocale: Locale) => {
		setLocaleState(newLocale);
		localStorage.setItem('locale', newLocale);
	}, []);

	const t = useCallback((key: TranslationKey): string => translations[locale][key], [locale]);

	const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

	return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = (): LocaleContextType => {
	const context = useContext(LocaleContext);
	if (!context) {
		throw new Error('useLocale must be used within a LocaleProvider');
	}

	return context;
};
